import { useMutation, useQuery } from '@tanstack/react-query-v4';
import {
  QMDeliveryBuilderFetchTemplateTaskResponse,
  QMDeliveryBuilderSetConfigExportEnabledResponse,
} from '@scale/shared/qm/qmRequestTypes';
import apiFetch, { ApiFetchOptions, HTTPError } from 'react-scale/lib/api-fetch';
import { queryFactory } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import { TaskStatus } from '@scale/shared-types';
import EnrichedError from 'react-scale/lib/enrichedError';
import {
  CustomTypeScriptFormatterPreviewRequest,
  CustomTypeScriptFormatterPreviewResponse,
} from '@scale/shared/chatDeliveryBuilder';
import { ITaskReturnObject } from '@scale/shared/types/return_object/task';

async function deliveryApiFetch<R = any, B = any>(
  url: string,
  options: ApiFetchOptions<B> = {},
): Promise<R> {
  try {
    return await apiFetch<R, B>(null, url, options);
  } catch (err) {
    if (err instanceof HTTPError) {
      // see tryParseErrorResponse() for how this gets populated
      throw new EnrichedError(
        err.status ?? 400,
        err.response.error,
        err.response.error,
        err.response.error,
        url,
      );
    } else {
      throw err;
    }
  }
}

// Direct API call to local formatter service
async function localFormatterApiFetch<R = any, B = any>(
  endpoint: string,
  options: { method?: 'GET' | 'POST'; body?: B } = {},
): Promise<R> {
  const { method = 'GET', body } = options;
  const url = `http://localhost:3106/${endpoint}`;

  const fetchOptions: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText };
      }

      throw new EnrichedError(
        response.status,
        `Local formatter service error: ${errorData.message || errorText}`,
        'LOCAL_FORMATTER_API_ERROR',
        `Request failed with ${response.status} with error: ${errorData.message || errorText}`,
        url,
      );
    }

    const result = await response.json();
    return result;
  } catch (err) {
    if (err instanceof EnrichedError) {
      throw err;
    }
    const errorMessage = err instanceof Error ? err.message : String(err);
    throw new EnrichedError(
      500,
      `Failed to connect to local formatter service at ${url}`,
      'LOCAL_FORMATTER_CONNECTION_ERROR',
      `Connection failed: ${errorMessage}`,
      url,
    );
  }
}

interface FetchRecentTaskArgs {
  projectId: string;
  status: TaskStatus;
  deliveryBuilderConfigId?: string;
}

export function useFetchRecentTask() {
  return useMutation<QMDeliveryBuilderFetchTemplateTaskResponse, string, FetchRecentTaskArgs>({
    mutationFn: async ({ projectId, status, deliveryBuilderConfigId }) => {
      const result: QMDeliveryBuilderFetchTemplateTaskResponse = await deliveryApiFetch(
        `corp-api/delivery-builder/fetch-recent-task`,
        {
          method: 'POST',
          body: { projectId, status, deliveryBuilderConfigId },
        },
      );
      return result;
    },
  });
}

export function useFetchRecentTaskId() {
  return useMutation<{ taskId: string }, string, FetchRecentTaskArgs>({
    mutationFn: async ({ projectId, status }) => {
      const result: { taskId: string } = await deliveryApiFetch(
        `corp-api/delivery-builder/fetch-recent-task-id`,
        {
          method: 'POST',
          body: { projectId, status },
        },
      );

      return result;
    },
  });
}

interface FetchTemplateTaskArgs {
  taskId: string;
  projectId: string;
  deliveryBuilderConfigId?: string;
}

export function useFetchTemplateTask() {
  return useMutation<QMDeliveryBuilderFetchTemplateTaskResponse, string, FetchTemplateTaskArgs>({
    mutationFn: async ({ taskId, projectId, deliveryBuilderConfigId }) => {
      const result: QMDeliveryBuilderFetchTemplateTaskResponse = await deliveryApiFetch(
        `corp-api/delivery-builder/fetch-template-task`,
        {
          method: 'POST',
          body: { taskId, projectId, deliveryBuilderConfigId },
        },
      );
      return result;
    },
  });
}

export function getFormatOnExportQueryKey(projectId: string) {
  return [...queryFactory.projects.all, 'useDeliveryBuilderTemplateForTaskExport', projectId];
}

export function useGetFormatOnExportConfig({ projectId }: { projectId: string }) {
  return useQuery<boolean | undefined>({
    queryKey: getFormatOnExportQueryKey(projectId),
    queryFn: async () => {
      const result: { success: boolean; enabled: boolean } = await deliveryApiFetch(
        `corp-api/delivery-builder/fetch-config-export-enabled`,
        {
          method: 'POST',
          body: { projectId },
        },
      );
      return result?.enabled;
    },
  });
}

export function useUpdateFormatOnExportConfig() {
  return useMutation<
    QMDeliveryBuilderSetConfigExportEnabledResponse,
    string,
    {
      projectId: string;
      enabled: boolean;
    }
  >({
    mutationFn: async ({ projectId, enabled }) => {
      const result = await deliveryApiFetch(`corp-api/delivery-builder/set-config-export-enabled`, {
        method: 'POST',
        body: { projectId, enabled },
      });
      return result?.newValue;
    },
  });
}

export function useHasExternalProjectQuery(projectId: string) {
  return useQuery<string | null, Error>({
    queryKey: [...queryFactory.projects.all, 'hasExternalProject', projectId] as const,
    queryFn: async () => {
      const result = await deliveryApiFetch(`corp-api/delivery-builder/projects/external_project`, {
        query: { projectId },
      });
      return result?.externalProjectId ?? null;
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useApplyCustomTsFormatterToTemplateTask() {
  return useMutation<
    CustomTypeScriptFormatterPreviewResponse,
    any,
    CustomTypeScriptFormatterPreviewRequest
  >({
    mutationFn: async ({
      taskId,
      customFormatterName,
      taskReturnObject,
      config,
      forceLocal,
      deliveryBuilderConfigId,
    }) => {
      if (forceLocal) {
        // Direct call to local formatter service
        const formatResult = await localFormatterApiFetch<Record<string, any>>('format', {
          method: 'POST',
          body: {
            taskId,
            projectId: taskReturnObject?.project,
            formatType: customFormatterName,
            taskReturnObject,
            config,
          },
        });

        // Transform the result to match the expected response format
        const response: CustomTypeScriptFormatterPreviewResponse = {
          formattedTask: formatResult,
          success: true,
        };
        return response;
      } else {
        // Regular backend API call
        const result = await deliveryApiFetch(
          `corp-api/delivery-builder/fetch-custom-ts-formatter-preview`,
          {
            method: 'POST',
            body: {
              taskId,
              customFormatterName,
              taskReturnObject,
              config,
              forceLocal,
              deliveryBuilderConfigId,
            },
          },
        );
        return result;
      }
    },
  });
}

export function useGetRecentDeliverySizes({ projectId }: { projectId: string }) {
  return useQuery<number[]>({
    queryKey: [...queryFactory.projects.all, 'recentDeliverySizes', projectId] as const,
    queryFn: async () => {
      const res = await deliveryApiFetch(`corp-api/delivery-builder/projects/delivery_sizes`, {
        query: { projectId },
      });
      return res;
    },
  });
}

export function useGetUniqueTaskStructures({ taskIds }: { taskIds: string[] }) {
  return useQuery<{ taskIds: string[] }>({
    queryKey: ['uniqueTaskStructures', taskIds] as const,
    queryFn: async () => {
      const res = await deliveryApiFetch(`corp-api/delivery-builder/fetch-unique-task-structures`, {
        method: 'POST',
        body: { taskIds },
      });
      return res;
    },
  });
}

export function useGetCustomerAuditTask({ taskId }: { taskId: string }) {
  return useQuery<{ task: ITaskReturnObject }>({
    queryKey: ['customerAuditTask', taskId] as const,
    queryFn: async () => {
      const res = await deliveryApiFetch(`corp-api/delivery-builder/customer-audit-task`, {
        query: { taskId },
      });
      return res;
    },
  });
}

export function useGetTypescriptFormatterNames(forceLocal?: boolean) {
  return useQuery<string[]>({
    queryKey: ['typescriptFormatterNames', forceLocal] as const,
    queryFn: async () => {
      if (forceLocal) {
        // Direct call to local formatter service
        const names = await localFormatterApiFetch<string[]>('formatterNames');
        return names;
      } else {
        // Regular backend API call
        const names = await deliveryApiFetch(
          `corp-api/delivery-builder/typescript_formatter_names`,
          {
            query: {
              forceLocal: forceLocal ? 'true' : undefined,
            },
          },
        );
        return names;
      }
    },
  });
}
